import classnames from 'classnames';
import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import { CrossIcon } from '@icons';

import styles from './DataListInput.module.scss';

type ConditionalProps =
  | {
      /**
       * If true, the onChange call back will receive a
       * string instead of a ChangeEvent
       */
      provideValueOnChange: true;
      onChange?: (value: string) => void;
    }
  | {
      provideValueOnChange?: false;
      onChange?: (event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    };

export type DataListInputProps = {
  /**
   * Provide a name if you're using the checkbox inside a form
   */
  name?: string;
  /**
   * Custom style class to extend the checkbox
   */
  className?: string;
  /**
   * Flag to disable the checkbox
   */
  disabled?: boolean;
  /**
   * Test id for Cypress and other test suites, maps to `data-testid`
   */
  testId?: string;
  value?: string;
  defaultValue?: string;
  placeholder?: string;
  listId?: string;
  items?: string[];
} & ConditionalProps;

const DataListInput = ({
  name,
  className,
  disabled,
  testId,
  value,
  defaultValue,
  placeholder,
  onChange,
  provideValueOnChange,
  listId,
  items,
}: DataListInputProps) => {
  const [localValue, setLocalValue] = useState(defaultValue || '');
  const inputRef = useRef<HTMLInputElement>(null);
  const actualListId = useMemo(() => listId || `dataGrid-${uuidv4()}`, []);

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setLocalValue(event.target.value);
    if (provideValueOnChange) {
      onChange?.(event.target.value);
    } else {
      onChange?.(event);
    }
  };

  const handleClear = () => {
    setLocalValue('');
    if (inputRef?.current) {
      handleOnChange({
        target: { ...inputRef.current, value: '' },
      } as ChangeEvent<HTMLInputElement>);
    }
  };

  useEffect(() => {
    if (typeof value === 'string') {
      setLocalValue(value);
    }
  }, [value]);

  return (
    <div className={styles.container}>
      {!listId && (
        <datalist id={actualListId}>
          {items?.map((item, index) => <option value={item} key={`item-${index}`} />)}
        </datalist>
      )}
      <input
        name={name}
        value={localValue}
        onChange={handleOnChange}
        className={classnames(styles.input, className)}
        ref={inputRef}
        disabled={disabled}
        placeholder={placeholder}
        data-testid={testId}
        list={actualListId}
        aria-label={name}
        role="textbox"
      />
      {!!localValue.length && ( // casting to bool as it was displaying 0 when falsy
        <button onClick={handleClear}>
          <CrossIcon size={16} />
        </button>
      )}
    </div>
  );
};

export { DataListInput };
